import { Button, Text } from '@southfields-digital/mpxlive-components';
import { useEffect, useMemo, useState } from 'react';

import { useMatchControlData } from 'src/components/DataController';
import { getRepeaterItems } from 'src/components/GraphicPropertiesForm';
import SportDataSelect from '../SportDataSelect';
import Repeater from 'src/components/Repeater';

import { getPropertyValues } from '../utils';
import { MemberTypeOptions, MemberTypes } from 'src/types/matchControl/member';
import { HockeyContestant } from 'src/types/matchControl/contestant';

type Props = {
  handleImportData: (values: PropertyValue[]) => void;
  selectedGraphic: PlaylistGraphic;
  selectedProperty: RepeaterProperties;
};

const HockeyLineup = ({ handleImportData, selectedGraphic, selectedProperty }: Props) => {
  const { data: matchControl, isLoading, error } = useMatchControlData();

  const defaultMemberType = useMemo<MemberTypes>(
    () => (selectedProperty.id === 'substitutionGroups' ? MemberTypes.BENCH : MemberTypes.STARTER),
    [selectedProperty.id]
  );

  const [memberType, setType] = useState<MemberTypes>(defaultMemberType);

  const { homeTeam, awayTeam } = useMemo(() => {
    if (matchControl?.sport !== 'hockey') return { homeTeam: null, awayTeam: null };
    const [home, away] = matchControl.contestants;
    return { homeTeam: home, awayTeam: away };
  }, [matchControl]);

  const getFilteredPropertyValues = (team: HockeyContestant | null) =>
    useMemo(() => {
      const members = team?.members?.filter((m) => m.type === memberType) || [];
      return getPropertyValues(selectedGraphic, members, []);
    }, [team, memberType, selectedGraphic]);

  const homePropertyValues = getFilteredPropertyValues(homeTeam);
  const awayPropertyValues = getFilteredPropertyValues(awayTeam);

  const homeRepeaterValues = useMemo(
    () => getRepeaterItems(selectedProperty, homePropertyValues),
    [selectedProperty, homePropertyValues]
  );

  const awayRepeaterValues = useMemo(
    () => getRepeaterItems(selectedProperty, awayPropertyValues),
    [selectedProperty, awayPropertyValues]
  );

  useEffect(() => {
    setType(defaultMemberType);
  }, [defaultMemberType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Text color="surface">Loading match control data...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <Text color="surface">Error loading match control data: {error}</Text>
      </div>
    );
  }

  if (!matchControl) {
    return (
      <div className="flex items-center justify-center py-12">
        <Text color="surface">No match control data available</Text>
      </div>
    );
  }

  if (matchControl.sport !== 'hockey') {
    return (
      <div className="flex items-center justify-center py-12">
        <Text color="surface">This component is only available for hockey matches</Text>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SportDataSelect
        label="Selected type"
        value={memberType}
        options={MemberTypeOptions}
        onChange={(e) => setType(e.currentTarget.value as MemberTypes)}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {homeTeam && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Text as="h3" className="font-semibold text-lg">
                {homeTeam.name || homeTeam.shortName} (Home)
              </Text>
              <Button
                onClick={() => handleImportData(homePropertyValues)}
                disabled={!homePropertyValues.length}
              >
                Import
              </Button>
            </div>
            <Repeater
              id="lineupRepeaterHome"
              onChangeRows={() => {}}
              items={homeRepeaterValues}
              isDisabled
              excludedIds={['divider', 'highlight']}
            />
          </div>
        )}

        {awayTeam && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Text as="h3" className="font-semibold text-lg">
                {awayTeam.name || awayTeam.shortName} (Away)
              </Text>
              <Button
                onClick={() => handleImportData(awayPropertyValues)}
                disabled={!awayPropertyValues.length}
              >
                Import
              </Button>
            </div>
            <Repeater
              id="lineupRepeaterAway"
              onChangeRows={() => {}}
              items={awayRepeaterValues}
              isDisabled
              excludedIds={['divider', 'highlight']}
            />
          </div>
        )}
      </div>

      {!homeTeam?.members?.length && !awayTeam?.members?.length && (
        <div className="text-center py-8">
          <Text color="surface">
            No member lineup data available for either team. Member lineups can be managed in the
            Match Control section.
          </Text>
        </div>
      )}
    </div>
  );
};

export default HockeyLineup;
